import { EnhancedTableHead, Order } from '@components/Table/EnhancedTableHead';
import {
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableRow,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { SortableCustomerProps, headerCells } from './NomineeCustomers.consts';
import { getComparator } from '@utils/table/tableUtils';
import { Button, Flex, Loader, Text } from '@components';
import { FormattedMessage } from 'react-intl';
import { Container, TableContainer } from './NomineeCustomers.styles';
import { useNomineeCustomersQuery } from '@features/api/nomineeCustomerSlice';
import { NewCustomerPanel } from './NewCustomerPanel/NewCustomerPanel';
import { useNavigate } from 'react-router-dom';

const rowsPerPage = 8;
const NomineeCustomers: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { data: nomineeCustomers, isLoading: isLoadingCustomers } =
    useNomineeCustomersQuery();

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof SortableCustomerProps>('id');
  const [page, setPage] = useState(1);

  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  const handleCloseSidePanel = () => {
    setIsSidePanelOpen(false);
  };

  const handleOpenSidePanel = () => {
    setIsSidePanelOpen(true);
  };

  const handleViewCustomer = (id: string) => {
    navigate(`/crm/customer/basic-details/${id}`);
  };

  const handleRequestSort = (
    _e: unknown,
    property: keyof SortableCustomerProps
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_e: unknown, newPage: number) => {
    setPage(newPage);
  };

  if (isLoadingCustomers) {
    return (
      <>
        KUR
        <Loader isLoading />;
      </>
    );
  }

  if (!nomineeCustomers) return <>KUR</>;

  const customersCount =
    nomineeCustomers.length > 0 ? nomineeCustomers.length : 0;
  const totalPages = Math.ceil(customersCount / rowsPerPage);

  return (
    <Container data-testid='customers-container'>
      <Flex my={6}>
        <Button onClick={handleOpenSidePanel}>
          <FormattedMessage id='crm.customer.addCustomer' />
        </Button>
      </Flex>
      <TableContainer>
        <Flex overflow='auto'>
          <Table aria-labelledby='tableTitle'>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              headerCells={headerCells}
            />
            <TableBody>
              {nomineeCustomers.length ? (
                nomineeCustomers
                  ?.slice()
                  .sort((a, b) => {
                    const result = getComparator<keyof SortableCustomerProps>(
                      order,
                      orderBy
                    )(a, b);
                    const equalValuesResult = 0;
                    // If the values are equal we additionally sort on the ID
                    if (result === equalValuesResult) {
                      const idComparisonResult = getComparator(order, 'id')(
                        a,
                        b
                      );
                      return idComparisonResult;
                    }

                    return result;
                  })
                  .slice(
                    (page - 1) * rowsPerPage,
                    (page - 1) * rowsPerPage + rowsPerPage
                  )
                  .map((row, index) => {
                    const labelId = `enhanced-table-checkbox-${row.id}`;
                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={row.id}
                        sx={{
                          backgroundColor:
                            index % 2 === 0
                              ? theme.palette.grey[100]
                              : theme.palette.common.white,
                        }}
                      >
                        <TableCell
                          component='th'
                          id={labelId}
                          scope='row'
                          padding='normal'
                          align='right'
                        >
                          {row.id}
                        </TableCell>
                        <TableCell align='left'>{row.firstName}</TableCell>
                        <TableCell align='left'>
                          {row.middleName !== '' ? row.middleName : '-'}
                        </TableCell>
                        <TableCell align='left'>{row.lastName}</TableCell>
                        <TableCell scope='' align='center' size='small'>
                          <Button
                            variant='tertiary'
                            onClick={() => handleViewCustomer(row.id)}
                          >
                            <FormattedMessage id='crm.viewCustomer' />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align='center'>
                    <Text
                      variant='h5'
                      fontWeight='fontWeightMedium'
                      sx={{ color: theme.palette.grey[700] }}
                    >
                      <FormattedMessage id='misc.noResults' />
                    </Text>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Flex>
        <Flex width='100%' justifyContent='center' padding={8}>
          <Pagination
            color='primary'
            shape='rounded'
            count={totalPages}
            page={page}
            onChange={handleChangePage}
          />
        </Flex>
      </TableContainer>

      <NewCustomerPanel
        isOpen={isSidePanelOpen}
        onClose={handleCloseSidePanel}
      />
    </Container>
  );
};

export default NomineeCustomers;
